import { array, boolean, discriminatedUnion, enum as enumFactory, iso, literal, strictObject, string } from "../../lazy-zod.js";
import { openMeldRequestContextSchema } from "../daemon/profile-identity.js";
import { organizationProtectedInformationSchema } from "./organization-information-protection.js";
import { centerAgentToolSpeakerSchema } from "./center-agent-speaker.js";
import { centerAgentMemberResearchReportSchema } from "./center-agent-member-research.js";
//#region src/schemas/http/organization-documents.ts
const ORGANIZATION_DOCUMENT_CONTENT_MAX_CHARS = 2e4;
const ORGANIZATION_DOCUMENT_TITLE_MAX_CHARS = 200;
const ORGANIZATION_DOCUMENT_FOLDER_NAME_MAX_CHARS = 200;
const ORGANIZATION_DOCUMENT_OPERATION_ID_MAX_CHARS = 256;
const ORGANIZATION_KNOWLEDGE_DOCUMENT_LIMIT = 50;
const ORGANIZATION_DOCUMENT_FOLDER_LIMIT = 50;
const CENTER_AGENT_PROFILE_REPORT_COMPUTER_ROOT = "/computer/home/documents/Profile Reports";
const CENTER_AGENT_PROFILE_REPORT_FORBIDDEN_PATH_CHARACTERS = "\\\\/:*?\"<>|";
const CENTER_AGENT_PROFILE_REPORT_COLLISION_SUFFIX_PATTERN = / \(\d+\)$/u;
const CENTER_AGENT_ORGANIZATION_DOCUMENT_DRAFT_CREATED_BY = "center_agent";
const ORGANIZATION_DOCUMENT_ERROR_CODES = [
	"organization_document.brief_exists",
	"organization_document.content_too_long",
	"organization_document.draft_changed",
	"organization_document.draft_not_found",
	"organization_document.folder_exists",
	"organization_document.folder_cycle",
	"organization_document.folder_limit_reached",
	"organization_document.folder_name_invalid",
	"organization_document.folder_not_found",
	"organization_document.human_actor_required",
	"organization_document.knowledge_limit_reached",
	"organization_document.managed_placement",
	"organization_document.member_profile_exists",
	"organization_document.not_found",
	"organization_document.operation_conflict",
	"organization_document.organization_context_changed",
	"organization_document.pending_draft_conflict",
	"organization_document.store_unavailable",
	"organization_document.subject_invalid",
	"organization_document.subject_not_member",
	"organization_document.title_invalid",
	"organization_document.write_forbidden",
	"organization_document.working_copy_conflict"
];
const organizationDocumentKindSchema = enumFactory([
	"brief",
	"member_profile",
	"knowledge"
]);
const profileReportTypeSchema = enumFactory(["work_profile"]);
const identifierSchema = string().trim().min(1).max(256);
const titleSchema = string().trim().min(1).max(200);
const folderNameSchema = string().trim().min(1).max(200);
function buildCenterAgentProfileReportComputerPath(input) {
	const folderName = sanitizeCenterAgentProfileReportPathSegment(input.profileReportFolderName);
	const documentTitle = sanitizeCenterAgentProfileReportPathSegment(input.documentTitle);
	return `${CENTER_AGENT_PROFILE_REPORT_COMPUTER_ROOT}/${folderName}/${documentTitle}.md`;
}
function sanitizeCenterAgentProfileReportPathSegment(value) {
	const sanitized = Array.from(value.normalize("NFKC"), (character) => {
		return (character.codePointAt(0) ?? 0) <= 31 || CENTER_AGENT_PROFILE_REPORT_FORBIDDEN_PATH_CHARACTERS.includes(character) ? " - " : character;
	}).join("").replace(/\s+/gu, " ").replace(/(?:\s+-\s+){2,}/gu, " - ").replace(/^[.\s]+|[.\s]+$/gu, "").trim();
	if (!sanitized) throw new Error("Profile Report files require a folder name and title.");
	const maxLength = 120;
	if (sanitized.length <= maxLength) return sanitized;
	const collisionSuffix = sanitized.match(CENTER_AGENT_PROFILE_REPORT_COLLISION_SUFFIX_PATTERN)?.[0];
	if (collisionSuffix && collisionSuffix.length < maxLength) return `${sanitized.slice(0, maxLength - collisionSuffix.length).trimEnd()}${collisionSuffix}`;
	return sanitized.slice(0, maxLength).trim();
}
const contentSchema = string().max(ORGANIZATION_DOCUMENT_CONTENT_MAX_CHARS);
const isoDateTimeSchema = iso.datetime({ offset: true });
const operationIdSchema = string().trim().min(1).max(256);
const organizationDocumentFolderSchema = strictObject({
	createdAt: isoDateTimeSchema,
	id: identifierSchema,
	isManaged: boolean().default(false),
	name: folderNameSchema,
	organizationId: identifierSchema,
	parentFolderId: identifierSchema.nullable().optional(),
	updatedAt: isoDateTimeSchema
});
const organizationDocumentSummarySchema = strictObject({
	canEditContent: boolean(),
	canManage: boolean(),
	createdAt: isoDateTimeSchema,
	folderId: identifierSchema.nullable(),
	hasDraft: boolean(),
	id: identifierSchema,
	kind: organizationDocumentKindSchema,
	organizationId: identifierSchema,
	profileReportDate: iso.date().nullable().optional(),
	profileReportType: profileReportTypeSchema.nullable().optional(),
	subjectUserId: identifierSchema.nullable(),
	title: titleSchema,
	updatedAt: isoDateTimeSchema
});
const organizationDocumentSchema = organizationDocumentSummarySchema.omit({ hasDraft: true }).extend({
	content: contentSchema,
	draftContent: contentSchema.nullable(),
	draftCreatedAt: isoDateTimeSchema.nullable(),
	draftCreatedBy: literal(CENTER_AGENT_ORGANIZATION_DOCUMENT_DRAFT_CREATED_BY).nullable()
});
const listOrganizationDocumentsQuerySchema = strictObject({});
const listOrganizationDocumentsResponseSchema = strictObject({
	canManage: boolean(),
	currentUserId: identifierSchema,
	documents: array(organizationDocumentSummarySchema),
	folders: array(organizationDocumentFolderSchema),
	organizationId: identifierSchema
});
const organizationDocumentRouteParamsSchema = strictObject({ documentId: identifierSchema });
const getOrganizationDocumentResponseSchema = strictObject({ document: organizationDocumentSchema });
const createOrganizationDocumentBodySchema = strictObject({
	content: contentSchema,
	expectedOrganizationId: identifierSchema,
	folderId: identifierSchema.nullable().optional(),
	kind: organizationDocumentKindSchema,
	subjectUserId: identifierSchema.nullable().optional(),
	title: titleSchema.optional()
}).superRefine(validateCreateDocumentShape);
const createOrganizationDocumentResponseSchema = strictObject({ document: organizationDocumentSchema });
const updateOrganizationDocumentBodySchema = strictObject({
	content: contentSchema.optional(),
	expectedDocumentUpdatedAt: isoDateTimeSchema.optional(),
	expectedDraftCreatedAt: isoDateTimeSchema.nullable().optional(),
	expectedOrganizationId: identifierSchema,
	folderId: identifierSchema.nullable().optional(),
	title: titleSchema.optional()
}).superRefine((value, context) => {
	if (value.content === void 0) return;
	if (value.expectedDocumentUpdatedAt === void 0) context.addIssue({
		code: "custom",
		message: "Saving content requires the loaded document version.",
		path: ["expectedDocumentUpdatedAt"]
	});
	if (value.expectedDraftCreatedAt === void 0) context.addIssue({
		code: "custom",
		message: "Saving content requires the loaded draft version.",
		path: ["expectedDraftCreatedAt"]
	});
}).refine((value) => value.content !== void 0 || value.title !== void 0 || value.folderId !== void 0, { message: "At least one document field must be updated." });
const updateOrganizationDocumentResponseSchema = strictObject({ document: organizationDocumentSchema });
const deleteOrganizationDocumentBodySchema = strictObject({ expectedOrganizationId: identifierSchema });
const deleteOrganizationDocumentResponseSchema = strictObject({
	deleted: literal(true),
	documentId: identifierSchema,
	organizationId: identifierSchema
});
const organizationDocumentFolderRouteParamsSchema = strictObject({ folderId: identifierSchema });
const createOrganizationDocumentFolderBodySchema = strictObject({
	expectedOrganizationId: identifierSchema,
	name: folderNameSchema,
	parentFolderId: identifierSchema.nullable().optional()
});
const createOrganizationDocumentFolderResponseSchema = strictObject({ folder: organizationDocumentFolderSchema });
const updateOrganizationDocumentFolderBodySchema = strictObject({
	expectedOrganizationId: identifierSchema,
	name: folderNameSchema.optional(),
	parentFolderId: identifierSchema.nullable().optional()
}).refine((value) => Number(value.name !== void 0) + Number(value.parentFolderId !== void 0) === 1, { message: "Exactly one folder field must be updated." });
const updateOrganizationDocumentFolderResponseSchema = strictObject({ folder: organizationDocumentFolderSchema });
const deleteOrganizationDocumentFolderBodySchema = strictObject({ expectedOrganizationId: identifierSchema });
const deleteOrganizationDocumentFolderResponseSchema = strictObject({
	deleted: literal(true),
	folderId: identifierSchema,
	organizationId: identifierSchema
});
const approveOrganizationDocumentDraftBodySchema = strictObject({
	expectedDraftCreatedAt: isoDateTimeSchema,
	expectedOrganizationId: identifierSchema
});
const approveOrganizationDocumentDraftResponseSchema = strictObject({ document: organizationDocumentSchema });
const discardOrganizationDocumentDraftBodySchema = strictObject({
	expectedDraftCreatedAt: isoDateTimeSchema,
	expectedOrganizationId: identifierSchema
});
const discardOrganizationDocumentDraftResponseSchema = strictObject({ document: organizationDocumentSchema });
const listOrganizationDocumentsRpcInputSchema = strictObject({ context: openMeldRequestContextSchema });
const getOrganizationDocumentRpcInputSchema = strictObject({
	context: openMeldRequestContextSchema,
	documentId: identifierSchema
});
const createOrganizationDocumentRpcInputSchema = createOrganizationDocumentBodySchema.extend({ context: openMeldRequestContextSchema });
const updateOrganizationDocumentRpcInputSchema = updateOrganizationDocumentBodySchema.extend({
	context: openMeldRequestContextSchema,
	documentId: identifierSchema
});
const deleteOrganizationDocumentRpcInputSchema = deleteOrganizationDocumentBodySchema.extend({
	context: openMeldRequestContextSchema,
	documentId: identifierSchema
});
const createOrganizationDocumentFolderRpcInputSchema = createOrganizationDocumentFolderBodySchema.extend({ context: openMeldRequestContextSchema });
const updateOrganizationDocumentFolderRpcInputSchema = updateOrganizationDocumentFolderBodySchema.extend({
	context: openMeldRequestContextSchema,
	folderId: identifierSchema
});
const deleteOrganizationDocumentFolderRpcInputSchema = deleteOrganizationDocumentFolderBodySchema.extend({
	context: openMeldRequestContextSchema,
	folderId: identifierSchema
});
const approveOrganizationDocumentDraftRpcInputSchema = approveOrganizationDocumentDraftBodySchema.extend({
	context: openMeldRequestContextSchema,
	documentId: identifierSchema
});
const discardOrganizationDocumentDraftRpcInputSchema = discardOrganizationDocumentDraftBodySchema.extend({
	context: openMeldRequestContextSchema,
	documentId: identifierSchema
});
const centerAgentReadOrganizationDocumentsRpcInputSchema = discriminatedUnion("action", [strictObject({
	action: literal("list"),
	context: openMeldRequestContextSchema,
	speakerMember: centerAgentToolSpeakerSchema
}), strictObject({
	action: literal("get"),
	context: openMeldRequestContextSchema,
	documentId: identifierSchema,
	speakerMember: centerAgentToolSpeakerSchema
})]);
const centerAgentReadOrganizationDocumentsResponseSchema = discriminatedUnion("action", [strictObject({
	action: literal("list"),
	documents: array(organizationDocumentSummarySchema.pick({
		folderId: true,
		id: true,
		kind: true,
		title: true,
		updatedAt: true
	})),
	folders: array(organizationDocumentFolderSchema),
	organizationId: identifierSchema
}), strictObject({
	action: literal("get"),
	document: organizationDocumentSchema.pick({
		content: true,
		folderId: true,
		id: true,
		kind: true,
		subjectUserId: true,
		title: true,
		updatedAt: true
	}),
	organizationId: identifierSchema
})]);
const organizationDocumentComputerProjectionSchema = organizationDocumentSchema.pick({
	content: true,
	folderId: true,
	id: true,
	kind: true,
	subjectUserId: true,
	title: true,
	updatedAt: true
});
const centerAgentOrganizationDocumentsComputerSnapshotRpcInputSchema = strictObject({ context: openMeldRequestContextSchema });
const centerAgentOrganizationDocumentsComputerSnapshotResponseSchema = strictObject({
	documents: array(organizationDocumentComputerProjectionSchema),
	folders: array(organizationDocumentFolderSchema),
	organizationId: identifierSchema,
	revision: string().regex(/^[a-f0-9]{64}$/u)
});
const centerAgentDraftOrganizationDocumentRpcInputSchema = strictObject({
	context: openMeldRequestContextSchema,
	documentId: identifierSchema.optional(),
	draftContent: contentSchema,
	expectedDocumentUpdatedAt: isoDateTimeSchema.optional(),
	folderId: identifierSchema.nullable().optional(),
	kind: organizationDocumentKindSchema.optional(),
	memberResearchReport: centerAgentMemberResearchReportSchema.optional(),
	memberResearchTaskId: identifierSchema.optional(),
	speakerMember: centerAgentToolSpeakerSchema,
	subjectUserId: identifierSchema.optional(),
	title: titleSchema.optional()
}).superRefine(validateCenterAgentDraftTarget);
const centerAgentDraftOrganizationDocumentResponseSchema = strictObject({
	document: organizationDocumentSummarySchema,
	draftReplaced: boolean(),
	memberResearchDraft: strictObject({
		content: contentSchema,
		documentId: identifierSchema,
		profileReportFolderName: folderNameSchema,
		subjectUserId: identifierSchema,
		title: titleSchema
	}).optional()
});
const recordCenterAgentMemberResearchComputerFileRpcInputSchema = strictObject({
	context: openMeldRequestContextSchema,
	documentId: identifierSchema,
	path: string().trim().min(1).max(1024),
	subjectUserId: identifierSchema,
	taskId: identifierSchema
});
const recordCenterAgentMemberResearchComputerFileResponseSchema = strictObject({
	documentId: identifierSchema,
	path: string().trim().min(1).max(1024),
	recorded: literal(true),
	taskId: identifierSchema
});
const centerAgentOrganizationDocumentProjectionSchema = organizationDocumentSchema.pick({
	content: true,
	folderId: true,
	id: true,
	kind: true,
	title: true,
	updatedAt: true
});
const centerAgentManageOrganizationDocumentsRpcInputSchema = discriminatedUnion("action", [
	strictObject({
		action: literal("create_folder"),
		context: openMeldRequestContextSchema,
		name: folderNameSchema,
		operationId: operationIdSchema,
		parentFolderId: identifierSchema.nullable().optional(),
		speakerMember: centerAgentToolSpeakerSchema
	}),
	strictObject({
		action: literal("rename_folder"),
		context: openMeldRequestContextSchema,
		folderId: identifierSchema,
		name: folderNameSchema,
		operationId: operationIdSchema,
		speakerMember: centerAgentToolSpeakerSchema
	}),
	strictObject({
		action: literal("move_document"),
		context: openMeldRequestContextSchema,
		documentId: identifierSchema,
		folderId: identifierSchema.nullable(),
		operationId: operationIdSchema,
		speakerMember: centerAgentToolSpeakerSchema
	}),
	strictObject({
		action: literal("save_document"),
		content: contentSchema,
		context: openMeldRequestContextSchema,
		documentId: identifierSchema.optional(),
		folderId: identifierSchema.nullable().optional(),
		operationId: operationIdSchema,
		speakerMember: centerAgentToolSpeakerSchema,
		title: titleSchema.optional()
	}).superRefine((value, context) => {
		if (!(value.documentId || value.title)) context.addIssue({
			code: "custom",
			message: "A new knowledge document requires a title.",
			path: ["title"]
		});
	})
]);
const centerAgentManageOrganizationDocumentsResponseSchema = discriminatedUnion("action", [
	strictObject({
		action: literal("create_folder"),
		folder: organizationDocumentFolderSchema,
		organizationId: identifierSchema
	}),
	strictObject({
		action: literal("rename_folder"),
		folder: organizationDocumentFolderSchema,
		organizationId: identifierSchema
	}),
	strictObject({
		action: literal("move_document"),
		document: centerAgentOrganizationDocumentProjectionSchema,
		organizationId: identifierSchema
	}),
	strictObject({
		action: literal("save_document"),
		document: centerAgentOrganizationDocumentProjectionSchema,
		organizationId: identifierSchema,
		outcome: enumFactory(["created", "updated"])
	})
]);
const organizationDocumentPromptContextRpcInputSchema = strictObject({
	context: openMeldRequestContextSchema,
	includeOrganizationProtectedInformation: literal(true).optional()
});
const organizationDocumentPromptContextResponseSchema = strictObject({
	briefContent: contentSchema.nullable(),
	documentTitles: array(titleSchema),
	organizationProtectedInformation: organizationProtectedInformationSchema.nullable().optional(),
	organizationId: identifierSchema
});
function validateCreateDocumentShape(value, context) {
	if (value.kind === "member_profile" && !value.subjectUserId) context.addIssue({
		code: "custom",
		message: "A Profile Report requires a subject user.",
		path: ["subjectUserId"]
	});
	if (value.kind !== "member_profile" && value.subjectUserId != null) context.addIssue({
		code: "custom",
		message: "Only a Profile Report can have a subject user.",
		path: ["subjectUserId"]
	});
	if (value.kind === "knowledge" && !value.title) context.addIssue({
		code: "custom",
		message: "A knowledge document requires a title.",
		path: ["title"]
	});
}
function validateCenterAgentDraftTarget(value, context) {
	if (Boolean(value.memberResearchTaskId) !== Boolean(value.memberResearchReport)) context.addIssue({
		code: "custom",
		message: "Member research drafts require both memberResearchTaskId and memberResearchReport.",
		path: ["memberResearchReport"]
	});
	if (value.documentId) {
		if (value.folderId !== void 0 || value.kind || value.subjectUserId || value.title) context.addIssue({
			code: "custom",
			message: "A document id cannot be combined with a new document target or folder placement."
		});
		return;
	}
	if (!value.kind) {
		context.addIssue({
			code: "custom",
			message: "A new draft requires a document kind.",
			path: ["kind"]
		});
		return;
	}
	if (value.expectedDocumentUpdatedAt) context.addIssue({
		code: "custom",
		message: "A working-copy version requires an existing document id.",
		path: ["expectedDocumentUpdatedAt"]
	});
	validateCreateDocumentShape({
		kind: value.kind,
		...value.subjectUserId ? { subjectUserId: value.subjectUserId } : {},
		...value.title ? { title: value.title } : {}
	}, context);
}
//#endregion
export { CENTER_AGENT_ORGANIZATION_DOCUMENT_DRAFT_CREATED_BY, CENTER_AGENT_PROFILE_REPORT_COMPUTER_ROOT, ORGANIZATION_DOCUMENT_CONTENT_MAX_CHARS, ORGANIZATION_DOCUMENT_ERROR_CODES, ORGANIZATION_DOCUMENT_FOLDER_LIMIT, ORGANIZATION_DOCUMENT_FOLDER_NAME_MAX_CHARS, ORGANIZATION_DOCUMENT_OPERATION_ID_MAX_CHARS, ORGANIZATION_DOCUMENT_TITLE_MAX_CHARS, ORGANIZATION_KNOWLEDGE_DOCUMENT_LIMIT, approveOrganizationDocumentDraftBodySchema, approveOrganizationDocumentDraftResponseSchema, approveOrganizationDocumentDraftRpcInputSchema, buildCenterAgentProfileReportComputerPath, centerAgentDraftOrganizationDocumentResponseSchema, centerAgentDraftOrganizationDocumentRpcInputSchema, centerAgentManageOrganizationDocumentsResponseSchema, centerAgentManageOrganizationDocumentsRpcInputSchema, centerAgentOrganizationDocumentsComputerSnapshotResponseSchema, centerAgentOrganizationDocumentsComputerSnapshotRpcInputSchema, centerAgentReadOrganizationDocumentsResponseSchema, centerAgentReadOrganizationDocumentsRpcInputSchema, createOrganizationDocumentBodySchema, createOrganizationDocumentFolderBodySchema, createOrganizationDocumentFolderResponseSchema, createOrganizationDocumentFolderRpcInputSchema, createOrganizationDocumentResponseSchema, createOrganizationDocumentRpcInputSchema, deleteOrganizationDocumentBodySchema, deleteOrganizationDocumentFolderBodySchema, deleteOrganizationDocumentFolderResponseSchema, deleteOrganizationDocumentFolderRpcInputSchema, deleteOrganizationDocumentResponseSchema, deleteOrganizationDocumentRpcInputSchema, discardOrganizationDocumentDraftBodySchema, discardOrganizationDocumentDraftResponseSchema, discardOrganizationDocumentDraftRpcInputSchema, getOrganizationDocumentResponseSchema, getOrganizationDocumentRpcInputSchema, listOrganizationDocumentsQuerySchema, listOrganizationDocumentsResponseSchema, listOrganizationDocumentsRpcInputSchema, organizationDocumentFolderRouteParamsSchema, organizationDocumentFolderSchema, organizationDocumentKindSchema, organizationDocumentPromptContextResponseSchema, organizationDocumentPromptContextRpcInputSchema, organizationDocumentRouteParamsSchema, organizationDocumentSchema, organizationDocumentSummarySchema, profileReportTypeSchema, recordCenterAgentMemberResearchComputerFileResponseSchema, recordCenterAgentMemberResearchComputerFileRpcInputSchema, updateOrganizationDocumentBodySchema, updateOrganizationDocumentFolderBodySchema, updateOrganizationDocumentFolderResponseSchema, updateOrganizationDocumentFolderRpcInputSchema, updateOrganizationDocumentResponseSchema, updateOrganizationDocumentRpcInputSchema };

//# sourceMappingURL=organization-documents.js.map